import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {StyledTransactionsPanel, StyledTransactionsPanelTable} from './StyledTransactionsPanel';
import Card from '../../../Card';
import LoadMoreButton from '../../TransactionsPage/LoadMoreButton';
import {amountHelpers, dateHelpers, transactionsHelpers} from '../../../../utils/helpers';

const groupedPropName = 'isMonthlyGroupedRow';
const isGroupedRow = transactionsHelpers[groupedPropName];

const gMV = amountHelpers.getAmountWithCurrencyCode;

const TransactionsPanel = ({
  dispatch,
  categories,
  edit = true,
  expenses = [],
  loading = false,
  loadMoreProps,
  onRowClick,
  selectedRowIndex,
  transactions = [],
  ...rest
}) => {
  const [t] = useTranslation(['main', 'transactions']);

  const tableData = useMemo(() => {
    const transactionsList = transactions.map(transaction => ({
      ...transaction,
      expense: expenses.find(e => e.id === transaction.expense_id)
    }));
    return transactionsHelpers.getGroupedTransactions(transactionsList);
  }, [expenses, transactions]);

  const columns = [
    {
      dataIndex: 'created_date',
      title: t('date'),
      render: (date, record) => {
        const isGrouped = isGroupedRow(record);
        return dateHelpers.getDateWithMonth(date, isGrouped ? 'MMM YYYY' : 'DD MMM');
      }
    },
    {
      dataIndex: 'source',
      title: t('transactions:source'),
      render: (_, transaction) => transactionsHelpers.getTransactionSourceWithIcon({categories, transaction}),
    },
    {
      dataIndex: 'payment_status',
      title: t('status'),
      render: (status, record) => {
        if (isGroupedRow(record)) return;
        const transactionStatus = transactionsHelpers.getTransactionStatus(status);
        return t(transactionStatus.key);
      }
    },
    {
      dataIndex: 'amount',
      title: t('amount'),
      render: (_, record) => {
        let amount;
        const getRecordNumberValue = (key) => record[key] || 0;
        if (isGroupedRow(record)) {
          amount = getRecordNumberValue('income') - getRecordNumberValue('outcome');
          return <span className='total-amount danger-text grouped-total'>{gMV(amount)}</span>;
        } else {
          let className = '';
          const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(record);
          const isIncoming = Boolean(record.is_incoming);
          amount = getRecordNumberValue('amount');
          if (!isIncoming && !isReturnedTransaction) className = `${className} danger-text`;
          if (isReturnedTransaction) className = `${className} returned-price`;
          return amount ? isIncoming ? gMV(amount) : <span className={className}>-{gMV(Math.abs(amount))}</span> : null;
        }
      }
    },
    {
      dataIndex: 'attachment',
      align: 'right',
      render: (_, transaction) => transactionsHelpers.getTransactionAttachmentIcon({transaction, translation: t}),
      width: 32
    }
  ];

  const handleOnRowClick = (e, record, index) => {
    const {id} = record;
    if (isGroupedRow(record)) return;

    onRowClick && onRowClick(id, index);
  };

  const getRowClassName = (record, index) => transactionsHelpers.getRowClassName({
    groupedPropName,
    index,
    record,
    selectedRowIndex
  });

  return (
    <StyledTransactionsPanel {...rest}>
      <Card
        title={t('transactions')}
      >
        <StyledTransactionsPanelTable
          columns={columns}
          dataSource={tableData}
          loading={loading}
          onRow={(record, index) => ({
            onClick: (e) => handleOnRowClick(e, record, index)
          })}
          pagination={false}
          rowClassName={getRowClassName}
          rowKey='id'
        />
        <LoadMoreButton
          {...loadMoreProps}
        >
          {t('loadMore')}
        </LoadMoreButton>
      </Card>
    </StyledTransactionsPanel>
  );
}

TransactionsPanel.propTypes = {
  edit: PropTypes.bool,
  expenses: PropTypes.array,
  loading: PropTypes.bool,
  loadMoreProps: PropTypes.object,
  onRowClick: PropTypes.func,
  selectedRowIndex: PropTypes.any,
  totalSpend: PropTypes.number,
  transactions: PropTypes.array,
}

const mapStateToProps = state => {
  const {categories} = state.main;
  return {
    categories,
  }
}

export default connect(mapStateToProps, null)(TransactionsPanel);
